import React, { useCallback, useEffect } from "react";
import { useGoogleMaps } from "react-hook-google-maps";
import { Geometry, LineString, Polygon } from "geojson";
import useTranslation from "src/translations/useTranslation";
import { MapConfig } from "@shared/constants/map.enum";
import mapDefaultParameters, { MAPS_API_VERSION } from "@utils/parameters";
import { GoogleApiRequestTypes } from "@shared/constants/google-api-request-types.enum";
import { useGoogleMapUsageLog } from "@hooks/useGoogleApiUsageLog";
import { useMapFullscreen } from "@hooks/useMapFullscreen";
import { ContainerMapFixedPointCoord } from "./styles";

type MapFixedPointCoordProps = {
  mapHeight: number,
  geometry: Geometry
  widenGeometry?: boolean
}
const MapFixedRegion = React.memo<MapFixedPointCoordProps>(({ mapHeight, geometry, widenGeometry }) => {

  const { i18n } = useTranslation();

  const GoogleApiKey = localStorage.getItem("@Fleet:google");
  const { ref, map } = useGoogleMaps(
    `${GoogleApiKey}&libraries=places,drawing&language=${i18n.language}&v=${MAPS_API_VERSION}`,
    {
      center: mapDefaultParameters.center,
      zoom: 15
    }
  );

  useGoogleMapUsageLog(map, GoogleApiRequestTypes.FIXED_REGION_MAP_LOAD);
  useMapFullscreen(map);

  /**
   * Widens a LineString geometry by creating a buffer around it
   */
  const widenLineString = useCallback((lineString: LineString): Polygon => {
    const coordinates = lineString.coordinates;
    const bufferDistance = MapConfig.VER_ZONE_BUFFER_DISTANCE;

    // Calculate perpendicular offsets for each segment
    const leftSide: number[][] = [];
    const rightSide: number[][] = [];

    for (let i = 0; i < coordinates.length - 1; i++) {
      const [x1, y1] = coordinates[i];
      const [x2, y2] = coordinates[i + 1];

      // Calculate segment direction
      const dx = x2 - x1;
      const dy = y2 - y1;
      const length = Math.sqrt(dx * dx + dy * dy);

      // Normalize and get perpendicular vector
      const perpX = -(dy / length);
      const perpY = dx / length;

      // Convert buffer distance from meters to degrees (approximate)
      const metersToDegrees = bufferDistance / 111320; // rough conversion at equator

      // Calculate offset points
      const offsetX = perpX * metersToDegrees;
      const offsetY = perpY * metersToDegrees;

      // Add points to left and right sides
      leftSide.push([x1 + offsetX, y1 + offsetY]);
      rightSide.push([x1 - offsetX, y1 - offsetY]);

      // Add the last point for the final segment
      if (i === coordinates.length - 2) {
        leftSide.push([x2 + offsetX, y2 + offsetY]);
        rightSide.push([x2 - offsetX, y2 - offsetY]);
      }
    }

    // Create polygon by combining left side + reversed right side + close the ring
    const polygonCoords = [...leftSide, ...rightSide.reverse(), leftSide[0]];

    return {
      type: "Polygon",
      coordinates: [polygonCoords]
    };
  }, []);

  /**
   * Draw map with point in coords passed in props
   */
  const drawGeometry = useCallback(async () => {
    if (geometry && map) {
      // Remove any existing data
      map.data.forEach((feature) => {
        map.data.remove(feature);
      });

      // Transform LineString geometries to 7-meter wide polygons
      let processedGeometry = geometry;

      if (geometry.type === "LineString" && widenGeometry) {
        processedGeometry = widenLineString(geometry as LineString);
      }

      // Create a GeoJSON Feature
      const feature = {
        type: "Feature",
        geometry: processedGeometry
      };

      // Add the GeoJSON Feature to the map
      map.data.addGeoJson(feature);

      // Optionally style the data layer
      map.data.setStyle({
        fillColor: "#000000",
        strokeColor: "#FFA500",
        strokeWeight: 2
      });

      // Fit map to the geometry's bounds
      const bounds = new google.maps.LatLngBounds();

      map.data.forEach((feature) => {
        feature.getGeometry().forEachLatLng((latlng) => {
          bounds.extend(latlng);
        });
      });
      map.fitBounds(bounds);
    }
  }, [geometry, map, widenGeometry, widenLineString]);

  useEffect(() => {

    // When map is render, draw according point
    if (map) drawGeometry().then();

  }, // drawPoint function duplicates request to Google API if it's present in dependency array
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [map, geometry]);

  return (
    <ContainerMapFixedPointCoord ref={ref} style={{ width: "100%", height: mapHeight }} />
  );
});

export default MapFixedRegion;
